import { Command } from 'commander';
import prompts from 'prompts';
import { KnowledgeBase } from '../core/knowledge-base';
import { logger } from '../utils/logger';
import pc from 'picocolors';

export function createKbCommand(): Command {
  const command = new Command('kb');
  command.description('Knowledge base management');

  // Search command
  command
    .command('search <term>')
    .description('Search knowledge base')
    .option('-t, --type <type>', 'Filter by type (pattern, decision, troubleshooting)')
    .option('-l, --limit <n>', 'Limit results', '10')
    .action(async (term, options) => {
      try {
        const kb = new KnowledgeBase();
        const results = await kb.search(term, {
          type: options.type,
          limit: parseInt(options.limit)
        });

        if (results.length === 0) {
          logger.info('No results found.');
          return;
        }

        logger.info(`\nFound ${pc.cyan(results.length)} results for "${pc.bold(term)}":\n`);

        results.forEach((entry, index) => {
          logger.info(`${pc.gray(`${index + 1}.`)} ${pc.bold(entry.title)}`);
          logger.info(`   ${pc.cyan(entry.type)} | ${pc.gray(entry.path)}`);
          
          // Show preview
          const preview = entry.preview.substring(0, 100);
          logger.info(`   ${preview}${entry.preview.length > 100 ? '...' : ''}`);
          logger.info('');
        });
      } catch (error: any) {
        logger.error('Search failed:', error.message);
        process.exit(1);
      }
    });

  // Add command
  command
    .command('add <type>')
    .description('Add to knowledge base')
    .option('-t, --title <title>', 'Entry title')
    .option('-c, --content <content>', 'Entry content')
    .option('-f, --file <file>', 'Read content from file')
    .option('--tags <tags>', 'Comma-separated tags')
    .action(async (type, options) => {
      try {
        const kb = new KnowledgeBase();
        
        // Validate type
        const validTypes = ['pattern', 'decision', 'troubleshooting', 'snippet'];
        if (!validTypes.includes(type)) {
          throw new Error(`Invalid type. Must be one of: ${validTypes.join(', ')}`);
        }

        // Get entry details
        let { title, content } = options;

        if (!title || (!content && !options.file)) {
          const answers = await prompts([
            {
              type: !title ? 'text' : null,
              name: 'title',
              message: 'Entry title:',
              validate: (value) => value.trim() ? true : 'Title is required'
            },
            {
              type: !content && !options.file ? 'text' : null,
              name: 'content',
              message: 'Entry content (or press Enter to open editor):',
              initial: ''
            }
          ]);

          title = title || answers.title;
          content = content || answers.content;
        }

        // Add to knowledge base
        const entry = await kb.add({
          type: type as any,
          title,
          content: content || '',
          tags: options.tags ? options.tags.split(',').map((t: string) => t.trim()) : []
        });

        logger.success(`✅ Added to knowledge base: ${entry.type}/${entry.id}`);
      } catch (error: any) {
        logger.error('Failed to add entry:', error.message);
        process.exit(1);
      }
    });

  // List command
  command
    .command('list')
    .description('List knowledge base entries')
    .option('-t, --type <type>', 'Filter by type')
    .option('-n, --limit <n>', 'Limit results', '20')
    .action(async (options) => {
      try {
        const kb = new KnowledgeBase();
        const entries = await kb.list({
          type: options.type,
          limit: parseInt(options.limit)
        });

        if (entries.length === 0) {
          logger.info('Knowledge base is empty.');
          return;
        }

        logger.info(`\n${pc.bold('Knowledge Base Entries')} (${entries.length}):\n`);

        const grouped = entries.reduce((acc, entry) => {
          if (!acc[entry.type]) acc[entry.type] = [];
          acc[entry.type].push(entry);
          return acc;
        }, {} as Record<string, typeof entries>);

        Object.entries(grouped).forEach(([type, typeEntries]) => {
          logger.info(pc.bold(`📁 ${type} (${typeEntries.length})`));
          
          typeEntries.forEach(entry => {
            logger.info(`  • ${entry.title}`);
            if (entry.tags.length > 0) {
              logger.info(`    ${pc.gray(`Tags: ${entry.tags.join(', ')}`)}`)              ;
            }
          });
          logger.info('');
        });
      } catch (error: any) {
        logger.error('Failed to list entries:', error.message);
        process.exit(1);
      }
    });

  return command;
}